/**
 * LeftShield Admin - Audit Log Page
 * View and search platform audit events
 */
import React, { useState } from 'react';
import {
  Title1,
  Body1,
  Body2,
  Card,
  CardHeader,
  Button,
  Badge,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Spinner,
  tokens,
  Input,
  Dropdown,
  Option,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
} from '@fluentui/react-components';
import {
  HistoryRegular,
  SearchRegular,
  FilterRegular,
  PersonRegular,
  BuildingRegular,
  CalendarRegular,
  InfoRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  ArrowClockwiseRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { auditApi } from '../services/api';
import type { AuditLogEntry, AuditAction, AuditLogFilter } from '../types';

const actionColors: Record<AuditAction, 'success' | 'warning' | 'danger' | 'informative' | 'brand'> = {
  TenantCreated: 'success',
  TenantUpdated: 'informative',
  TenantSuspended: 'warning',
  TenantReactivated: 'success',
  TrialStarted: 'brand',
  TrialConverted: 'success',
  TrialExpired: 'danger',
  UserLogin: 'informative',
  UserLogout: 'informative',
  ApiKeyCreated: 'brand',
  ApiKeyRevoked: 'warning',
  ConfigurationChanged: 'warning',
};

const actionLabels: Record<AuditAction, string> = {
  TenantCreated: 'Tenant Created',
  TenantUpdated: 'Tenant Updated',
  TenantSuspended: 'Tenant Suspended',
  TenantReactivated: 'Tenant Reactivated',
  TrialStarted: 'Trial Started',
  TrialConverted: 'Trial Converted',
  TrialExpired: 'Trial Expired',
  UserLogin: 'User Login',
  UserLogout: 'User Logout',
  ApiKeyCreated: 'API Key Created',
  ApiKeyRevoked: 'API Key Revoked',
  ConfigurationChanged: 'Configuration Changed',
};

const allActions: AuditAction[] = [
  'TenantCreated',
  'TenantUpdated',
  'TenantSuspended',
  'TenantReactivated',
  'TrialStarted',
  'TrialConverted',
  'TrialExpired',
  'UserLogin',
  'UserLogout',
  'ApiKeyCreated',
  'ApiKeyRevoked',
  'ConfigurationChanged',
];

export const AuditPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [filter, setFilter] = useState<AuditLogFilter>({});
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: auditData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['auditLog', page, pageSize, filter],
    queryFn: () => auditApi.getEntries(filter, page, pageSize),
  });

  const handleFilterChange = (key: keyof AuditLogFilter, value: string | undefined) => {
    setFilter(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilter({});
    setSearchTerm('');
    setPage(1);
  };

  const filteredEntries = auditData?.items?.filter(entry => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      entry.actorName?.toLowerCase().includes(term) ||
      entry.actorEmail?.toLowerCase().includes(term) ||
      entry.targetTenantName?.toLowerCase().includes(term) ||
      entry.action.toLowerCase().includes(term)
    );
  }) ?? [];

  if (error) {
    return (
      <Card style={{ margin: '1rem', padding: '1rem' }}>
        <Body1 style={{ color: tokens.colorPaletteRedForeground1 }}>
          Error loading audit log: {error instanceof Error ? error.message : 'Unknown error'}
        </Body1>
      </Card>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <HistoryRegular style={{ fontSize: '2rem' }} />
          <div>
            <Title1>Audit Log</Title1>
            <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
              {auditData?.totalCount ?? 0} total events
            </Body2>
          </div>
        </div>
        <Button
          appearance="secondary"
          icon={<ArrowClockwiseRegular />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: '1rem' }}>
        <CardHeader
          image={<FilterRegular />}
          header={<Body1 style={{ fontWeight: 600 }}>Filters</Body1>}
        />
        <div style={{ padding: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 200px' }}>
            <Body2 style={{ marginBottom: '0.25rem' }}>Search</Body2>
            <Input
              value={searchTerm}
              onChange={(_, data) => setSearchTerm(data.value)}
              placeholder="Search by name, email, tenant..."
              contentBefore={<SearchRegular />}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: '0 0 180px' }}>
            <Body2 style={{ marginBottom: '0.25rem' }}>Action</Body2>
            <Dropdown
              value={filter.action ? actionLabels[filter.action] : 'All Actions'}
              selectedOptions={filter.action ? [filter.action] : []}
              onOptionSelect={(_, data) => handleFilterChange('action', data.optionValue as AuditAction)}
              style={{ width: '100%' }}
            >
              <Option value="">All Actions</Option>
              {allActions.map(action => (
                <Option key={action} value={action}>
                  {actionLabels[action]}
                </Option>
              ))}
            </Dropdown>
          </div>
          <div style={{ flex: '0 0 150px' }}>
            <Body2 style={{ marginBottom: '0.25rem' }}>Start Date</Body2>
            <Input
              type="date"
              value={filter.startDate || ''}
              onChange={(_, data) => handleFilterChange('startDate', data.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: '0 0 150px' }}>
            <Body2 style={{ marginBottom: '0.25rem' }}>End Date</Body2>
            <Input
              type="date"
              value={filter.endDate || ''}
              onChange={(_, data) => handleFilterChange('endDate', data.value)}
              style={{ width: '100%' }}
            />
          </div>
          <Button appearance="subtle" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>
      </Card>

      {/* Audit Log Table */}
      <Card>
        <CardHeader
          header={
            <Body1 style={{ fontWeight: 600 }}>
              Events {auditData && `(${filteredEntries.length} of ${auditData.totalCount})`}
            </Body1>
          }
        />
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <Spinner size="medium" label="Loading audit log..." />
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Timestamp</TableHeaderCell>
                  <TableHeaderCell>Action</TableHeaderCell>
                  <TableHeaderCell>Actor</TableHeaderCell>
                  <TableHeaderCell>Target Tenant</TableHeaderCell>
                  <TableHeaderCell>IP Address</TableHeaderCell>
                  <TableHeaderCell style={{ width: '50px' }}>Details</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.map((entry: AuditLogEntry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <CalendarRegular />
                        <div>
                          <div>{new Date(entry.timestamp).toLocaleDateString()}</div>
                          <div style={{ fontSize: '0.75rem', color: tokens.colorNeutralForeground3 }}>
                            {new Date(entry.timestamp).toLocaleTimeString()}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge appearance="filled" color={actionColors[entry.action]}>
                        {actionLabels[entry.action]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {entry.actorName ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <PersonRegular />
                          <div>
                            <div style={{ fontWeight: 500 }}>{entry.actorName}</div>
                            <div style={{ fontSize: '0.75rem', color: tokens.colorNeutralForeground3 }}>
                              {entry.actorEmail}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: tokens.colorNeutralForeground3 }}>System</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {entry.targetTenantName ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <BuildingRegular />
                          <span>{entry.targetTenantName}</span>
                        </div>
                      ) : (
                        <span style={{ color: tokens.colorNeutralForeground3 }}>-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <code style={{ fontSize: '0.75rem' }}>{entry.ipAddress || '-'}</code>
                    </TableCell>
                    <TableCell>
                      <Button
                        appearance="subtle"
                        icon={<InfoRegular />}
                        size="small"
                        onClick={() => setSelectedEntry(entry)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {filteredEntries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <Body1>No audit events found</Body1>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {auditData && auditData.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', padding: '1rem' }}>
                <Button
                  appearance="subtle"
                  icon={<ChevronLeftRegular />}
                  disabled={!auditData.hasPreviousPage}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <Body2>
                  Page {auditData.pageNumber} of {auditData.totalPages}
                </Body2>
                <Button
                  appearance="subtle"
                  icon={<ChevronRightRegular />}
                  iconPosition="after"
                  disabled={!auditData.hasNextPage}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Event Details Dialog */}
      <Dialog open={!!selectedEntry} onOpenChange={(_, data) => !data.open && setSelectedEntry(null)}>
        <DialogSurface style={{ maxWidth: '600px' }}>
          <DialogBody>
            <DialogTitle>Event Details</DialogTitle>
            <DialogContent>
              {selectedEntry && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingTop: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem' }}>
                    <Body2 style={{ fontWeight: 600 }}>Event ID:</Body2>
                    <Body2><code>{selectedEntry.id}</code></Body2>

                    <Body2 style={{ fontWeight: 600 }}>Timestamp:</Body2>
                    <Body2>{new Date(selectedEntry.timestamp).toLocaleString()}</Body2>

                    <Body2 style={{ fontWeight: 600 }}>Action:</Body2>
                    <Badge appearance="filled" color={actionColors[selectedEntry.action]}>
                      {actionLabels[selectedEntry.action]}
                    </Badge>

                    <Body2 style={{ fontWeight: 600 }}>Actor:</Body2>
                    <Body2>
                      {selectedEntry.actorName ? (
                        <>
                          {selectedEntry.actorName}
                          {selectedEntry.actorEmail && ` (${selectedEntry.actorEmail})`}
                        </>
                      ) : (
                        'System'
                      )}
                    </Body2>

                    {selectedEntry.targetTenantName && (
                      <>
                        <Body2 style={{ fontWeight: 600 }}>Target Tenant:</Body2>
                        <Body2>{selectedEntry.targetTenantName}</Body2>
                      </>
                    )}

                    <Body2 style={{ fontWeight: 600 }}>IP Address:</Body2>
                    <Body2><code>{selectedEntry.ipAddress || '-'}</code></Body2>

                    {selectedEntry.userAgent && (
                      <>
                        <Body2 style={{ fontWeight: 600 }}>User Agent:</Body2>
                        <Body2 style={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>
                          {selectedEntry.userAgent}
                        </Body2>
                      </>
                    )}
                  </div>

                  {selectedEntry.details && Object.keys(selectedEntry.details).length > 0 && (
                    <div>
                      <Body2 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Additional Details:</Body2>
                      <pre style={{
                        backgroundColor: tokens.colorNeutralBackground3,
                        padding: '0.75rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        overflow: 'auto',
                        maxHeight: '200px',
                      }}>
                        {JSON.stringify(selectedEntry.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setSelectedEntry(null)}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
